import { useDialogs } from 'hooks/dialogs'
import { XSmallIcon } from '../icons/XSmall'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'

export function DialogCloseButton({
  onClick,
  className,
  iconClassName,
  buttonRef,
  onKeyDown,
  onKeyUp,
  closeOnMouseDown,
}: {
  className?: string
  iconClassName?: string
  buttonRef?: React.Ref<HTMLButtonElement>
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
} & (
  | {
      onClick?: (
        e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      ) => boolean
      /** use closeOnMouseDown ONLY if popDialog is idempotent, i.e. there is only one dialog! */
      closeOnMouseDown?: never
    }
  | {
      onClick?: never
      /** use closeOnMouseDown ONLY if popDialog is idempotent, i.e. there is only one dialog! */
      closeOnMouseDown?: boolean
    }
)) {
  const { popDialog } = useDialogs()
  const { t } = useTranslation()

  const onClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
      if (onClick) {
        const res = onClick(e)
        if (res) popDialog()
      } else {
        popDialog()
      }
    },
    [onClick, popDialog]
  )

  return (
    // The wrapper takes the caller's padding, so its onClick makes that padding
    // a larger mouse target for the button below. Keyboard users have the
    // button. Clicks on the button itself bubble here; skip those so the
    // handler (and popDialog) runs once.
    <div
      role="presentation"
      className={classnames(
        'absolute top-4 right-4 flex justify-end',
        className
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClickHandler(e)
      }}
    >
      <button
        type="button"
        data-testid="dialog-close-button"
        className="focus-ring-double rounded-full p-1"
        ref={buttonRef}
        aria-label={t('dialogs.close_dialog')}
        onClick={onClickHandler}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onMouseDown={closeOnMouseDown ? popDialog : undefined}
      >
        <XSmallIcon aria-hidden size={22} className={iconClassName} />
      </button>
    </div>
  )
}
